import { z } from 'zod';

import { calendarDateSchema } from './employee';
import { wallClockTimeSchema } from './job';

/*
 * Availability. reference/data-model.md §5, business-rules.md §4, ROADMAP.md
 * §7.1 and §7.2.
 *
 * **One resolution order, stated once:** an override for the date wins
 * outright; otherwise the weekly row for that day of week; and if neither says
 * working, **the employee is unavailable**. The default is unavailable rather
 * than available, which is the whole reason a cleaner with no rows is not
 * silently bookable.
 *
 * `break_minutes` is unpaid and comes out of hours worked before the weekly
 * overtime threshold is applied (business-rules.md §5). It does **not** block
 * scheduling — a break is not a fixed slot — so nothing here says when it falls.
 */

/** 0 = Sunday … 6 = Saturday. The same convention as `WEEKDAY_LABELS`. */
export const dayOfWeekSchema = z
  .number()
  .int()
  .min(0, 'A day of the week is 0 to 6')
  .max(6, 'A day of the week is 0 to 6');

const breakMinutesSchema = z
  .number()
  .int('Give whole minutes')
  .min(0, 'A break cannot be negative')
  .max(720, 'That is more break than a working day');

/**
 * One row of the weekly template.
 *
 * The times are optional in the *shape* and required by the `refine` when the
 * day is a working one, so a form that clears the hours and leaves the toggle
 * on is refused here rather than by a database CHECK — the person needs to be
 * told which field, and a constraint violation cannot say.
 */
export const weeklyScheduleDaySchema = z
  .object({
    dayOfWeek: dayOfWeekSchema,
    isWorking: z.boolean(),
    startTime: wallClockTimeSchema.nullable().optional(),
    endTime: wallClockTimeSchema.nullable().optional(),
    breakMinutes: breakMinutesSchema.default(0),
  })
  .refine((value) => !value.isWorking || (value.startTime != null && value.endTime != null), {
    message: 'A working day needs a start and a finish',
    path: ['startTime'],
  })
  .refine(
    (value) =>
      !value.isWorking ||
      value.startTime == null ||
      value.endTime == null ||
      value.endTime > value.startTime,
    { message: 'The finish has to be after the start', path: ['endTime'] },
  )
  .refine((value) => !value.isWorking || value.breakMinutes < spanOf(value), {
    // A two-hour break out of a one-hour shift is arithmetic nobody meant, and
    // it would otherwise compute as negative hours worked on a payroll screen.
    message: 'The break is longer than the shift it comes out of',
    path: ['breakMinutes'],
  });

export type WeeklyScheduleDayInput = z.infer<typeof weeklyScheduleDaySchema>;

/**
 * The whole week, saved in one call.
 *
 * **All seven days each time, not a patch.** Saving a day at a time makes
 * "Monday off" and "Monday not filled in yet" the same request, and the two mean
 * opposite things once the resolver treats absence as unavailable. One call also
 * means the hours badge and the weekly total the screen shows are computed from
 * exactly what was sent.
 */
export const saveWeeklyScheduleSchema = z.object({
  days: z
    .array(weeklyScheduleDaySchema)
    .length(7, 'A week has seven days')
    .refine((days) => new Set(days.map((day) => day.dayOfWeek)).size === 7, {
      message: 'Each day of the week appears once',
    }),
});

export type SaveWeeklyScheduleInput = z.infer<typeof saveWeeklyScheduleSchema>;

/** A one-off. `isAvailable: false` is "off that day"; true is "in, differently". */
export const saveOverrideSchema = z
  .object({
    date: calendarDateSchema,
    isAvailable: z.boolean(),
    startTime: wallClockTimeSchema.nullable().optional(),
    endTime: wallClockTimeSchema.nullable().optional(),
    breakMinutes: breakMinutesSchema.default(0),
    reason: z
      .string()
      .trim()
      .max(255, 'Must be 255 characters or fewer')
      .transform((value) => (value.length === 0 ? null : value))
      .nullable()
      .optional(),
  })
  .refine((value) => !value.isAvailable || (value.startTime != null && value.endTime != null), {
    message: 'Working that day means saying from when until when',
    path: ['startTime'],
  })
  .refine(
    (value) =>
      !value.isAvailable ||
      value.startTime == null ||
      value.endTime == null ||
      value.endTime > value.startTime,
    { message: 'The finish has to be after the start', path: ['endTime'] },
  )
  .refine((value) => !value.isAvailable || value.breakMinutes < spanOf(value), {
    message: 'The break is longer than the shift it comes out of',
    path: ['breakMinutes'],
  });

export type SaveOverrideInput = z.infer<typeof saveOverrideSchema>;

/**
 * A bounded window, not a paginated list — P8, and the same cap as
 * `listJobsInRange` and `/api/employee/schedule`. The visible range *is* the
 * limit, which is what stops a calendar-shaped read growing without one.
 */
export const availabilityRangeQuerySchema = z.object({
  from: calendarDateSchema,
  to: calendarDateSchema,
});

export type AvailabilityRangeQuery = z.infer<typeof availabilityRangeQuerySchema>;

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

export interface WeeklyScheduleDayView {
  dayOfWeek: number;
  /** "Sunday". Resolved on the server so no screen renders a number (P5). */
  dayLabel: string;
  isWorking: boolean;
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number;
  /** Span minus break. `0` on a day off — zero is `0`, never a dash (P1). */
  workingMinutes: number;
}

export interface WeeklyScheduleView {
  employeeId: string;
  employeeName: string;
  days: WeeklyScheduleDayView[];
  /** The header's running figure: days worked and minutes across the week. */
  totals: { daysWorking: number; minutes: number };
}

export interface ScheduleOverrideView {
  id: string;
  employeeId: string;
  date: string;
  isAvailable: boolean;
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number;
  reason: string | null;
  workingMinutes: number;
}

/** Where a day's answer came from. `none` is the unavailable default. */
export type AvailabilitySource = 'override' | 'weekly' | 'none';

/**
 * One employee on one date, after the three rules have been applied.
 *
 * `explanation` is a whole sentence, not a fragment, because it is carried
 * straight into the auto-assignment ranking as the reason somebody was
 * disqualified (7.4) — and a ranking with no reasons is a black box the office
 * stops trusting the first time it is wrong.
 */
export interface DayAvailability {
  employeeId: string;
  date: string;
  dayOfWeek: number;
  isAvailable: boolean;
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number;
  /** Span minus break — the paid figure. */
  workingMinutes: number;
  source: AvailabilitySource;
  explanation: string;
}

/** The team list down the left of the scheduling screen — ROADMAP.md §7.2. */
export interface SchedulingTeamMember {
  employeeId: string;
  name: string;
  positionName: string | null;
  maxJobsPerDay: number;
  daysWorking: number;
  weeklyMinutes: number;
  /** Overrides still ahead of today — "2 upcoming date overrides". */
  upcomingOverrides: number;
}

// ---------------------------------------------------------------------------
// Presets — FINDINGS.md §2, the prototype's five
// ---------------------------------------------------------------------------

/**
 * The five named patterns from the prototype's scheduling screen.
 *
 * Preserved deliberately, names included: the office has been using them, and
 * filling in seven days by hand for every cleaner is the friction that leaves
 * `employee_weekly_schedule` empty — which the resolver reads as *unavailable*
 * for everybody.
 *
 * Shared rather than browser-only so a test can assert the arithmetic without
 * rendering anything, and so the same numbers could be applied server-side if a
 * future import ever wants them.
 */
export interface SchedulePreset {
  readonly id: string;
  readonly label: string;
  readonly days: readonly number[];
  readonly startTime: string;
  readonly endTime: string;
  readonly breakMinutes: number;
}

export const SCHEDULE_PRESETS: readonly SchedulePreset[] = [
  {
    id: 'mon-fri-8-5',
    label: 'Mon–Fri 8–5',
    days: [1, 2, 3, 4, 5],
    startTime: '08:00:00',
    endTime: '17:00:00',
    breakMinutes: 30,
  },
  {
    id: 'mon-fri-9-6',
    label: 'Mon–Fri 9–6',
    days: [1, 2, 3, 4, 5],
    startTime: '09:00:00',
    endTime: '18:00:00',
    breakMinutes: 30,
  },
  {
    id: 'mon-sat-8-5',
    label: 'Mon–Sat 8–5',
    days: [1, 2, 3, 4, 5, 6],
    startTime: '08:00:00',
    endTime: '17:00:00',
    breakMinutes: 30,
  },
  {
    id: 'tue-sat-9-5',
    label: 'Tue–Sat 9–5',
    days: [2, 3, 4, 5, 6],
    startTime: '09:00:00',
    endTime: '17:00:00',
    breakMinutes: 30,
  },
  {
    id: 'part-time-mwf',
    label: 'Part-time M/W/F',
    days: [1, 3, 5],
    startTime: '09:00:00',
    endTime: '14:00:00',
    breakMinutes: 0,
  },
];

// ---------------------------------------------------------------------------
// Arithmetic — one implementation, shared
// ---------------------------------------------------------------------------

/** `HH:MM[:SS]` → minutes since midnight. */
export function minutesSinceMidnight(time: string): number {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/** Start to finish in minutes, ignoring the break. */
function spanOf(value: { startTime?: string | null; endTime?: string | null }): number {
  if (value.startTime == null || value.endTime == null) return 0;
  return minutesSinceMidnight(value.endTime) - minutesSinceMidnight(value.startTime);
}

/**
 * Hours a day is worth: the span, less the unpaid break, never below zero.
 *
 * **Shared between the server and the badge on the scheduling screen**, because
 * a row that reads `8h 30m` beside a total that was computed differently is the
 * kind of disagreement nobody can debug from a screenshot.
 */
export function workingMinutesFor(value: {
  isWorking: boolean;
  startTime?: string | null;
  endTime?: string | null;
  breakMinutes?: number;
}): number {
  if (!value.isWorking) return 0;
  return Math.max(0, spanOf(value) - (value.breakMinutes ?? 0));
}
